import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { VerifyOtpForm } from '@/components/auth/VerifyOtpForm';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { showSuccessToast, showErrorToast } from '@/utils/toast-utils';

const VerifyOtp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);
  
  // Get the email, type, and returnTo from the location state
  const { email, type, returnTo } = location.state || {};
  
  useEffect(() => {
    // If there's no email or type, redirect to login
    if (!email || !type) {
      console.log("Missing required verification information, redirecting to login");
      showErrorToast({
        title: "Error",
        description: "Missing required verification information"
      });
      navigate('/login', { replace: true });
    }
  }, [email, type, navigate]);

  if (!email || !type) {
    return null; // Will redirect in useEffect
  }

  const title = type === 'signup' ? 'Verify Your Email' : 'Verify Password Reset';
  const description = type === 'signup'
    ? `Enter the 6-digit code sent to ${email} to activate your account.`
    : `Enter the 6-digit code sent to ${email} to proceed with password reset.`;

  const handleVerificationSuccess = () => {
    setIsSuccess(true);
    
    if (type === 'signup') {
      showSuccessToast({
        title: "Email Verified!",
        description: "Your account is now active."
      });
      
      // Allow time for the success message to be visible
      setTimeout(() => {
        const finalReturnTo = returnTo || '/dashboard';
        console.log("Signup verified, navigating to:", finalReturnTo);
        navigate(finalReturnTo, { replace: true });
      }, 1500);
    } else if (type === 'recovery') {
      showSuccessToast({
        title: "Identity Verified",
        description: "You can now reset your password."
      });
      
      // Allow time for the success message to be visible
      setTimeout(() => {
        console.log("Recovery verified, navigating to /password-reset");
        navigate('/password-reset', { replace: true });
      }, 1500);
    }
  };

  if (isSuccess) {
    return (
      <div className="w-full min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
        <div className="w-full max-w-md space-y-6 p-8 bg-card rounded-sm border border-border text-center">
          <div className="flex justify-center">
            <CheckCircle className="h-16 w-16 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Verification Successful</h1>
          <p className="text-muted-foreground">
            {type === 'signup' 
              ? 'Your email has been verified. You will be redirected shortly.' 
              : 'Your identity has been verified. You will be redirected to reset your password.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-6 p-8 bg-card rounded-sm border border-border">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          <p className="text-muted-foreground mt-2">{description}</p>
        </div>
        
        <VerifyOtpForm 
          email={email} 
          type={type} 
          returnTo={returnTo} 
          onSuccess={handleVerificationSuccess} 
        />

        <div className="text-center">
          <Button 
            variant="link" 
            onClick={() => navigate("/login")} 
            className="text-secondary"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Login
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
